import { AfterViewInit, Component, ElementRef, EventEmitter, Input, Output } from '@angular/core';
import { TipChoice, TipSettings, parseTipSettings, sameTipChoice } from '../../shared/order-tip';

/**
 * "Tip" — No tip, the venue's percentages, and optionally an amount of the guest's own.
 *
 * It used to live in the cart, under the promo code. Tips are now for online payment only
 * (the server drops the tip from an order paid any other way), and the method is chosen
 * AFTER Order is tapped, so the picker moved to where the method is chosen: the dine-in
 * payment sheet and the delivery/pickup form. This component is what the two share.
 *
 * ⚠️ **It never applies a tip.** It emits the guest's choice and the host sends it through
 * CartService.applyTip(); the server prices it and the host feeds the result back in as
 * `selected` / `amount`. Same split as the cart had: nothing here computes money.
 *
 * The custom-amount field, and the two errors it can raise on its own (not a number, above
 * the venue's maximum), are local — they never reach the server. Errors the SERVER raises
 * come in through `error`.
 */
@Component({
  selector: 'app-tip-picker',
  templateUrl: './tip-picker.component.html',
  styleUrls: ['./tip-picker.component.scss'],
  standalone: false,
})
export class TipPickerComponent implements AfterViewInit {
  @Input() settings: TipSettings = parseTipSettings(null);
  /** The choice on the order, as the server last confirmed it (or the one just sent). */
  @Input() selected: TipChoice = { kind: 'none' };
  /** The tip the server says is on the order, e.g. 2.00. 0 for none. */
  @Input() amount = 0;
  /** May contain HTML entities — rendered with [innerHTML], as everywhere else. */
  @Input() currencySymbol = '';
  /** A server refusal, as an `orderDetails.tip.errors.*` key ('' for none). */
  @Input() error = '';
  @Input() disabled = false;
  /**
   * Scroll the picker into view when it appears.
   *
   * On by default: both hosts show it at the bottom of a scrolling sheet, right after the
   * guest picked Online payment, which is exactly when it lands below the fold. A host
   * turns it off when the picker appears without the guest doing anything (the delivery
   * form opening with Online already chosen) — jumping the form to its bottom then would
   * hide the fields they still have to fill in.
   */
  @Input() revealOnShow = true;

  @Output() choose = new EventEmitter<TipChoice>();

  customOpen = false;
  customInput = '';
  /** Raised here, by the custom field: 'invalidAmount' | 'aboveMaximum' | ''. */
  localError = '';

  constructor(private host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    if (this.revealOnShow) {
      this.reveal();
    }
  }

  /** Pick "No tip" or one of the venue's percentages. */
  select(choice: TipChoice): void {
    this.localError = '';
    this.customOpen = false;
    this.choose.emit(choice);
  }

  openCustom(): void {
    this.localError = '';
    this.customOpen = true;
    this.customInput = this.selected.kind === 'fixed' ? String(this.selected.amount) : '';
  }

  /** A fixed amount, e.g. 5, whatever the order total. Accepts a comma as the decimal mark. */
  applyCustom(): void {
    const amount = Math.round(parseFloat(this.customInput.replace(',', '.')) * 100) / 100;
    if (!isFinite(amount) || amount <= 0) {
      this.localError = 'invalidAmount';
      return;
    }
    if (this.settings.maxAmount !== null && amount > this.settings.maxAmount) {
      this.localError = 'aboveMaximum';
      return;
    }
    this.select({ kind: 'fixed', amount });
  }

  isSelected(choice: TipChoice): boolean {
    return sameTipChoice(choice, this.selected);
  }

  /** The guest's own mistake first: it is about what they just typed. */
  get errorKey(): string {
    return this.localError || this.error;
  }

  /** The venue's highest custom tip, e.g. "100.00 ден", named in the "above maximum" error. */
  get maxLabel(): string {
    const max = this.settings?.maxAmount;
    return max == null ? '' : `${max.toFixed(2)} ${this.currencySymbol}`.trim();
  }

  /**
   * Scroll the nearest scrolling ancestor just far enough that the whole picker shows.
   *
   * Not `scrollIntoView()`: that scrolls EVERY scrollable ancestor, the page included, and
   * both hosts are position:fixed sheets that slide in with a transform — mid-animation the
   * picker sits below the viewport, and the page underneath the sheet would scroll too.
   *
   * After a frame, so the sheet has laid the picker out before it is measured.
   */
  reveal(): void {
    if (typeof window === 'undefined' || typeof requestAnimationFrame === 'undefined') return;

    requestAnimationFrame(() => {
      const el = this.host.nativeElement;
      const container = scrollingAncestor(el);
      if (!container) return;

      const box = el.getBoundingClientRect();
      const view = container.getBoundingClientRect();
      // Bottom edge into view, unless the picker is taller than the container: then its top.
      let delta = box.bottom - view.bottom;
      if (box.height > view.height) delta = box.top - view.top;
      if (delta <= 0) return;

      container.scrollTo({
        top: container.scrollTop + delta,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });
  }
}

/** The closest ancestor that actually scrolls — overflow set AND more content than room. */
function scrollingAncestor(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/** Read per call so a mid-session OS change is respected (as in the attractions banner). */
function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
